import { useState } from "react";
import { toast } from "sonner";
import { api, track } from "../lib/api";
import { CONTACT, EXPERIENCES } from "../data/content";

const initial = {
  full_name: "", mobile: "", email: "", event_type: "", event_date: "", city: "",
  venue: "", guest_count: "", indoor_outdoor: "", celebrity_required: "",
  entertainment: "", production_req: "", hospitality_req: "", security_req: "",
  media_req: "", budget: "", vision: "", contact_method: "Phone", consent: false, website: "",
};

export const BriefForm = () => {
  const [form, setForm] = useState(initial);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    track("event_brief_started", {});
    if (!form.consent) {
      track("form_error", { form: "brief" });
      toast.error("Please accept the consent checkbox to continue.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/briefs", form);
      track("event_brief_submitted", { event_type: form.event_type, city: form.city });
      track("form_success", { form: "brief" });
      setSubmitted(true);
    } catch (err) {
      track("form_error", { form: "brief" });
      toast.error("Something went wrong. Please try WhatsApp or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const whatsappSummary = () => {
    const msg = `Hello Mega Celebrity Events, I would like to plan an event.\nName: ${form.full_name}\nEvent Type: ${form.event_type}\nEvent Date: ${form.event_date}\nCity: ${form.city}\nExpected Guests: ${form.guest_count}\nVision: ${form.vision}`;
    track("whatsapp_click", { location: "brief_form" });
    window.open(`https://wa.me/919052910159?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (submitted) {
    return (
      <div className="border border-[#C9A55C]/30 bg-[#0D1018] p-10 text-center" data-testid="brief-success-message">
        <p className="font-display text-2xl text-[#C9A55C] mb-4">Thank you.</p>
        <p className="text-[#A9ABB2] text-sm leading-relaxed max-w-md mx-auto">
          Our event team will review your vision and contact you to discuss the next stage.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="event-brief-form">
      <input className="lux-input" placeholder="Full Name *" required value={form.full_name} onChange={set("full_name")} data-testid="brief-name-input" />
      <input className="lux-input" placeholder="Mobile Number *" required value={form.mobile} onChange={set("mobile")} data-testid="brief-mobile-input" />
      <input className="lux-input" type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="brief-email-input" />
      <select className="lux-input" value={form.event_type} onChange={set("event_type")} required data-testid="brief-event-type-select">
        <option value="">Event Type *</option>
        {EXPERIENCES.map((x) => <option key={x.slug} value={x.title}>{x.title}</option>)}
        <option value="Other">Other</option>
      </select>
      <input className="lux-input" type="date" value={form.event_date} onChange={set("event_date")} data-testid="brief-date-input" />
      <input className="lux-input" placeholder="City" value={form.city} onChange={set("city")} data-testid="brief-city-input" />
      <input className="lux-input" placeholder="Venue (if confirmed)" value={form.venue} onChange={set("venue")} data-testid="brief-venue-input" />
      <input className="lux-input" placeholder="Expected Guest Count" value={form.guest_count} onChange={set("guest_count")} data-testid="brief-guests-input" />
      <select className="lux-input" value={form.indoor_outdoor} onChange={set("indoor_outdoor")} data-testid="brief-indoor-outdoor-select">
        <option value="">Indoor or Outdoor?</option>
        <option>Indoor</option>
        <option>Outdoor</option>
        <option>Both</option>
      </select>
      <select className="lux-input" value={form.celebrity_required} onChange={set("celebrity_required")} data-testid="brief-celebrity-select">
        <option value="">Celebrity or Artist Required?</option>
        <option>Yes — Celebrity Appearance</option>
        <option>Yes — Live Performance</option>
        <option>Not Sure Yet</option>
        <option>No</option>
      </select>
      <input className="lux-input" placeholder="Entertainment Requirements" value={form.entertainment} onChange={set("entertainment")} data-testid="brief-entertainment-input" />
      <input className="lux-input" placeholder="Production Requirements (stage, sound, LED…)" value={form.production_req} onChange={set("production_req")} data-testid="brief-production-input" />
      <input className="lux-input" placeholder="Hospitality Requirements" value={form.hospitality_req} onChange={set("hospitality_req")} data-testid="brief-hospitality-input" />
      <input className="lux-input" placeholder="Security / Bouncer Requirements" value={form.security_req} onChange={set("security_req")} data-testid="brief-security-input" />
      <input className="lux-input" placeholder="Photography / Media Requirements" value={form.media_req} onChange={set("media_req")} data-testid="brief-media-input" />
      <select className="lux-input" value={form.budget} onChange={set("budget")} data-testid="brief-budget-select">
        <option value="">Estimated Budget Range (optional)</option>
        <option>Under ₹5 Lakhs</option>
        <option>₹5 – 15 Lakhs</option>
        <option>₹15 – 50 Lakhs</option>
        <option>₹50 Lakhs – 1 Crore</option>
        <option>Above ₹1 Crore</option>
      </select>
      <textarea
        className="lux-input sm:col-span-2 min-h-[110px]"
        placeholder="Additional Vision or Requirements"
        value={form.vision}
        onChange={set("vision")}
        data-testid="brief-vision-textarea"
      />
      <select className="lux-input" value={form.contact_method} onChange={set("contact_method")} data-testid="brief-contact-method-select">
        <option>Phone</option>
        <option>WhatsApp</option>
        <option>Email</option>
      </select>
      <label className="flex items-start gap-3 text-xs text-[#A9ABB2] leading-relaxed" data-testid="brief-consent-label">
        <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-0.5 accent-[#C9A55C]" data-testid="brief-consent-checkbox" />
        I agree to be contacted by Mega Celebrity Events about my event enquiry.
      </label>
      <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex="-1" autoComplete="off" />
      <div className="sm:col-span-2 flex flex-wrap gap-4 pt-2">
        <button type="submit" disabled={loading} className="btn-gold disabled:opacity-60" data-testid="event-brief-submit-btn">
          {loading ? "Sending…" : "Create My Event Brief"}
        </button>
        <button type="button" onClick={whatsappSummary} className="btn-ghost" data-testid="brief-whatsapp-btn">
          Continue on WhatsApp
        </button>
      </div>
    </form>
  );
};
